import type { Recipe, RecipeId, RecipeStore, Unsubscribe } from '@platewell/core';
import MiniSearch, { type SearchResult } from 'minisearch';

export interface SearchHit {
  id: RecipeId;
  score: number;
  /** Field names that matched, so the UI can say *why* a result appeared. */
  matchedFields: string[];
}

interface IndexedRecipe {
  id: RecipeId;
  title: string;
  description: string;
  ingredients: string;
  instructions: string;
  tags: string;
  notes: string;
  source: string;
}

const FIELDS: (keyof Omit<IndexedRecipe, 'id'>)[] = [
  'title',
  'description',
  'ingredients',
  'instructions',
  'tags',
  'notes',
  'source',
];

function toDocument(recipe: Recipe): IndexedRecipe {
  return {
    id: recipe.id,
    title: recipe.title,
    description: recipe.description,
    ingredients: recipe.ingredients.map((i) => `${i.quantity} ${i.unit} ${i.name} ${i.note}`).join(' '),
    instructions: recipe.instructions.map((i) => i.text).join(' '),
    tags: recipe.tags.join(' '),
    notes: recipe.notes,
    source: `${recipe.sourceUrl} ${recipe.sourceText}`,
  };
}

/**
 * Full-text search over the household, held entirely in memory on the device.
 *
 * Search has to work offline, which rules out asking the server, and a household of a few
 * thousand recipes indexes in well under a second — so the index is rebuilt from the CRDT
 * rather than persisted. Nothing to migrate, nothing to invalidate, no way for it to drift
 * out of step with the document.
 *
 * Updates are incremental: on each document change only recipes whose indexed text actually
 * changed are re-added, because reindexing everything on every keystroke would make typing
 * in a large household stutter.
 */
export class RecipeSearchIndex {
  private readonly index: MiniSearch<IndexedRecipe>;
  /** Last indexed text per recipe, used to skip no-op reindexing. */
  private readonly fingerprints = new Map<RecipeId, string>();
  private unsubscribe: Unsubscribe | null = null;

  constructor(private readonly store: RecipeStore) {
    this.index = new MiniSearch<IndexedRecipe>({
      fields: FIELDS,
      storeFields: ['id'],
      searchOptions: {
        prefix: true,
        fuzzy: 0.2,
        // Every word has to match: typing more words should narrow the list, not widen it.
        combineWith: 'AND',
        // Title matches should outrank an incidental word buried in step 7.
        boost: { title: 4, tags: 2, ingredients: 1.5 },
      },
    });
    this.rebuild();
  }

  /** Keeps the index in step with the document until `destroy` is called. */
  start(): this {
    this.unsubscribe ??= this.store.subscribeAll(() => this.refresh());
    return this;
  }

  private rebuild(): void {
    this.index.removeAll();
    this.fingerprints.clear();
    for (const recipe of this.store.listRecipes({ limit: Number.MAX_SAFE_INTEGER })) {
      this.add(recipe);
    }
  }

  private add(recipe: Recipe): void {
    const document = toDocument(recipe);
    this.index.add(document);
    this.fingerprints.set(recipe.id, fingerprint(document));
  }

  refresh(): void {
    const live = this.store.listRecipes({ limit: Number.MAX_SAFE_INTEGER });
    const seen = new Set<RecipeId>();

    for (const recipe of live) {
      seen.add(recipe.id);
      const document = toDocument(recipe);
      const next = fingerprint(document);
      const previous = this.fingerprints.get(recipe.id);
      if (previous === next) continue;
      if (previous != null) this.index.discard(recipe.id);
      this.index.add(document);
      this.fingerprints.set(recipe.id, next);
    }

    for (const id of [...this.fingerprints.keys()]) {
      if (seen.has(id)) continue;
      this.index.discard(id);
      this.fingerprints.delete(id);
    }
  }

  search(term: string, limit = 100): SearchHit[] {
    const trimmed = term.trim();
    if (trimmed === '') return [];
    return (this.index.search(trimmed) as SearchResult[]).slice(0, limit).map((result) => ({
      id: result.id as RecipeId,
      score: result.score,
      matchedFields: Object.keys(result.match).flatMap((token) => result.match[token] ?? []),
    }));
  }

  /** Type-ahead completions drawn from the indexed vocabulary. */
  suggest(term: string, limit = 8): string[] {
    const trimmed = term.trim();
    if (trimmed === '') return [];
    return this.index
      .autoSuggest(trimmed, { fuzzy: 0.2 })
      .slice(0, limit)
      .map((suggestion) => suggestion.suggestion);
  }

  get size(): number {
    return this.index.documentCount;
  }

  destroy(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.index.removeAll();
    this.fingerprints.clear();
  }
}

function fingerprint(document: IndexedRecipe): string {
  return FIELDS.map((field) => document[field]).join('\u0000');
}
